import { createContext, useContext } from 'react';

import { PAGE_BACKGROUND } from '@/theme';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export interface ThemeContextValue {
    preference: ThemePreference;
    resolvedTheme: ResolvedTheme;
    setPreference: (preference: ThemePreference) => void;
}

// Also read by the pre-paint script in index.html, which has to stay in step with it
export const THEME_STORAGE_KEY = 'theme-preference';

// Widened to `string | null` so a raw storage value can be looked up without an assertion
const PREFERENCES: ReadonlySet<string | null> = new Set<ThemePreference>(['light', 'dark', 'system']);

export const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const isThemePreference = (value: string | null): value is ThemePreference => PREFERENCES.has(value);

const prefersDark = (): MediaQueryList => window.matchMedia('(prefers-color-scheme: dark)');

export const getSystemTheme = (): ResolvedTheme => (prefersDark().matches ? 'dark' : 'light');

// localStorage throws SecurityError when site data is blocked; such a visitor follows the system theme
export const getStoredTheme = (): ThemePreference => {
    try {
        const stored = localStorage.getItem(THEME_STORAGE_KEY);
        return isThemePreference(stored) ? stored : 'system';
    } catch {
        return 'system';
    }
};

// Not remembered when site data is blocked: the choice holds until the page reloads
export const storeTheme = (preference: ThemePreference): void => {
    try {
        localStorage.setItem(THEME_STORAGE_KEY, preference);
    } catch {
        // nothing to do
    }
};

export const resolveTheme = (preference: ThemePreference, systemTheme: ResolvedTheme): ResolvedTheme =>
    preference === 'system' ? systemTheme : preference;

/**
 * Publishes the resolved theme to the document: the class the pre-paint <style> in index.html keys off, the
 * `color-scheme` native controls (scrollbars, inputs) read, and the browser chrome colour of mobile browsers.
 * The page itself is painted by ThemeProvider's global styles.
 */
export const applyThemeToDocument = (resolvedTheme: ResolvedTheme): void => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(resolvedTheme);
    root.style.colorScheme = resolvedTheme;
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', PAGE_BACKGROUND[resolvedTheme]);
};

export const initializeTheme = (): void => {
    applyThemeToDocument(resolveTheme(getStoredTheme(), getSystemTheme()));
};

export const subscribeToSystemTheme = (onChange: () => void): (() => void) => {
    const mediaQuery = prefersDark();
    mediaQuery.addEventListener('change', onChange);
    return () => mediaQuery.removeEventListener('change', onChange);
};

// Not `useTheme`: that is MUI's hook for the palette
export const useThemePreference = (): ThemeContextValue => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useThemePreference must be used within a ThemeProvider');
    }
    return context;
};
